import React from 'react';
import PropTypes from 'prop-types';
import bowser from 'yola-bowser';
import { Modal, Panel, PanelGroup, designSystem } from '@yola/ws-ui';
import Breadcrumbs from 'src/js/modules/common/components/breadcrumbs';
import constants from '../constants';

const {
  ActionButton,
  Stack,
  HorizontalStack,
  InfoWithPopover,
  ColorPalette,
  Box,
  DialogHeader,
  DialogButtonGroup,
  DialogButton,
  DialogButtonGroupSeparator,
  Heading,
  ColorScheme,
  Paragraph,
} = designSystem;

const { DEFAULT_OFFSET_Y, DEFAULT_OFFSET_X, DEFAULT_DIALOG_HEIGHT, DEFAULT_DIALOG_WIDTH } =
  constants;

const CustomPaletteDialog = ({
  offsetX = DEFAULT_OFFSET_X,
  offsetY = DEFAULT_OFFSET_Y,
  captions: {
    title,
    description,
    buttons,
    popoverInfo,
    titleBreadcrumbs,
    customPaletteLabel,
    colorSchemesLabel,
    resetTooltipLabel,
  },
  onCancel,
  onSubmit,
  onDragEnd = Function.prototype,
  onColorSelect,
  colorSchemeOptions,
  previewColors,
  onColorSchemeReset,
  onOverlayClick,
  onDeveloperModeClick = undefined,
  onEditPaletteClick,
  onAddColorScheme,
}) => (
  <Modal
    draggable
    resizable={false}
    fullscreen={bowser.mobile}
    width={DEFAULT_DIALOG_WIDTH}
    height={DEFAULT_DIALOG_HEIGHT}
    offsetX={offsetX}
    offsetY={offsetY}
    dragHandleSelector=".ws-drag-trigger"
    dragCancelSelector=".ws-ui-dialog-header-controls"
    overlay="none"
    handleCancel={onCancel}
    handleSubmit={onSubmit}
    onDragEnd={onDragEnd}
    onOverlayClick={onOverlayClick}
  >
    <PanelGroup height="100%">
      <Panel
        align="middle"
        corners="squared"
        className="ws-drag-trigger"
        style={{ flexShrink: '0' }}
      >
        <DialogHeader paddingBottom="spacing-s">
          <Box>
            <Box paddingBottom="spacing-2-xs">
              <Breadcrumbs breadcrumbs={titleBreadcrumbs} />
            </Box>
            <Box>
              <Stack>
                <Heading type="heading-3">{title}</Heading>
                <Paragraph>{description}</Paragraph>
              </Stack>
            </Box>
          </Box>
        </DialogHeader>
      </Panel>
      <Panel height="100%" scrollable style={{ borderBottom: 'none' }}>
        <Box
          paddingTop="spacing-s"
          paddingBottom="spacing-s"
          paddingLeft="spacing-m"
          paddingRight="spacing-m"
        >
          <Stack>
            <div className="ws-custom-palette-dialog__palette-preview">
              <Box marginBottom="spacing-2-xs">
                <Heading type="heading-6">{customPaletteLabel}</Heading>
              </Box>
              <ColorPalette colors={previewColors} layout="2-row" size="large" />
            </div>
            <Box marginTop="spacing-3-xs">
              <ActionButton label={buttons.editPalette} onClick={onEditPaletteClick} />
            </Box>
            <Box marginTop="spacing-m" marginBottom="spacing-3-xs">
              <HorizontalStack verticalAlign="center">
                <Heading type="heading-6">{colorSchemesLabel}</Heading>
                <InfoWithPopover content={popoverInfo} placement="top-center" />
              </HorizontalStack>
            </Box>
            <Stack gap="spacing-3-xs">
              {colorSchemeOptions.map(
                ({ value, label, backgroundColor, isDarkTheme, isResetAvailable }) => (
                  <ColorScheme
                    key={value}
                    title={label}
                    backgroundColor={backgroundColor}
                    isDarkTheme={isDarkTheme}
                    onEditClick={() => onColorSelect({ colorSchemeId: value })}
                    {...(isResetAvailable && {
                      resetTooltipLabel,
                      onResetClick: () => onColorSchemeReset({ colorSchemeId: value }),
                    })}
                    {...(onDeveloperModeClick && {
                      onDesignConfigure: () => onDeveloperModeClick({ colorSchemeId: value }),
                    })}
                  />
                )
              )}
            </Stack>
            <Box marginTop="spacing-2-xs">
              <ActionButton label={buttons.addColorScheme} onClick={onAddColorScheme} />
            </Box>
          </Stack>
        </Box>
      </Panel>
      <Panel corners="squared" style={{ flexShrink: '0' }}>
        <DialogButtonGroup>
          <DialogButton onClick={onSubmit}>{buttons.submit}</DialogButton>
          <DialogButtonGroupSeparator />
          <DialogButton onClick={onCancel}>{buttons.cancel}</DialogButton>
        </DialogButtonGroup>
      </Panel>
    </PanelGroup>
  </Modal>
);

CustomPaletteDialog.propTypes = {
  offsetX: PropTypes.number,
  offsetY: PropTypes.number,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onColorSelect: PropTypes.func.isRequired,
  onDragEnd: PropTypes.func,
  captions: PropTypes.shape({
    popoverInfo: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    titleBreadcrumbs: PropTypes.arrayOf(PropTypes.string),
    customPaletteLabel: PropTypes.string,
    colorSchemesLabel: PropTypes.string,
    resetTooltipLabel: PropTypes.string,
    buttons: PropTypes.shape({
      submit: PropTypes.string,
      cancel: PropTypes.string,
      editPalette: PropTypes.string,
      addColorScheme: PropTypes.string,
    }),
  }).isRequired,
  colorSchemeOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      backgroundColor: PropTypes.string.isRequired,
      isResetAvailable: PropTypes.bool.isRequired,
    })
  ).isRequired,
  previewColors: PropTypes.arrayOf(PropTypes.string).isRequired,
  onOverlayClick: PropTypes.func.isRequired,
  onDeveloperModeClick: PropTypes.oneOfType([PropTypes.func, PropTypes.oneOf([null])]),
  onColorSchemeReset: PropTypes.func.isRequired,
  onEditPaletteClick: PropTypes.func.isRequired,
  onAddColorScheme: PropTypes.func.isRequired,
};

export default CustomPaletteDialog;
